import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { Box, Link, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { GetApp } from '@material-ui/icons';
import { FileUpload, NewFileUploadArea } from '@roc/ui';
import { Document } from '@roc/feature-types';
import { LoanProcess } from '@roc/feature-utils';
import { useDocumentStore } from '../../../hooks/useDocumentStore';

interface Props {
  document: Document;
  onUploaded?: () => void;
}

// The shared NewFileUploadArea left-aligns its cloud icon inside an auto-width block, so
// it looks off-center against the centered helper text. Center the icon here (scoped to
// the dropzone only, so the "Download" link icon above is untouched).
const useStyles = makeStyles(() => ({
  dropzone: {
    '& .MuiSvgIcon-root': {
      display: 'block',
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
}));

/**
 * Self-contained upload control for the Extension Agreement line item.
 *
 * It deliberately does NOT reuse LineItemUpload: that component gates the dropzone on
 * `document.canUploadFile`, which for the "extensions" loan process resolves through an
 * entitlements path that has no extensions branch (so it is unreliable), and it also hides
 * the dropzone once a file exists. Here the dropzone is always shown so a file can be
 * uploaded or replaced at any time, and the current file is listed separately with a
 * download link. Uploads are filed under the task's taskId, which is scoped to this
 * specific extension.
 */
export const ExtensionAgreementUpload = observer(({ document, onUploaded }: Props) => {
  const classes = useStyles();
  const { documentStore } = useDocumentStore();
  const [uploads, setUploads] = useState<FileUpload[]>([]);
  const [uploading, setUploading] = useState(false);
  const [currentFileName, setCurrentFileName] = useState<string | null>(
    document?.canDownloadFile ? document?.originalFileName ?? null : null
  );

  useEffect(() => {
    const pending = uploads[0]?.file;
    if (!pending) return;
    setUploading(true);
    documentStore.uploadDocument(pending, document, LoanProcess.EXTENSIONS, () => {
      setCurrentFileName(pending.name);
      // Clear the pending item so the dropzone stays visible for a replacement upload.
      setUploads([]);
      setUploading(false);
      onUploaded?.();
    });
  }, [uploads]);

  const handleDownload = () => {
    documentStore.downloadDocument(document);
  };

  return (
    <Box>
      {currentFileName && (
        <Box display="flex" alignItems="center" mb={1}>
          <Typography variant="body2" style={{ marginRight: 8 }}>
            Current file: {currentFileName}
          </Typography>
          <Link
            component="button"
            variant="body2"
            onClick={handleDownload}
            style={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <GetApp fontSize="small" style={{ marginRight: 4 }} />
            Download
          </Link>
        </Box>
      )}
      <Box className={classes.dropzone}>
        <NewFileUploadArea
          items={uploads}
          setItems={setUploads}
          itemDocument={document}
          multiple={false}
          showUploadButton
        />
      </Box>
      {uploading && (
        <Typography variant="caption">Uploading...</Typography>
      )}
    </Box>
  );
});

export default ExtensionAgreementUpload;
